import { Injectable } from '@angular/core';

import { Observable } from 'rxjs';
import { map, shareReplay, switchMap, take } from 'rxjs/operators';

import {
    ApiFragment,
    ApiQuery,
    Combine,
    QueryPart,
    RecordPhase,
    RecordRestrictions,
    Restriction,
    ShAbstractHttpService,
    ShPaginationService,
    SortDirection,
    SortInfo,
    ZoneRestrictions,
    ZonesService
} from '@stylehaven/components';
import { RelationType } from '../../entities/mediahaven-api/relation-type.entity';
import { SearchService } from './search.service';

@Injectable({ providedIn: 'root' })
export class RelationsService {
    protected readonly relationTypesEndpoint: string = `v2/relation-types`;
    protected readonly mediaEndpoint: string = `v2/records`;

    private relationTypes$: Observable<RelationType[]>;

    constructor(
        private readonly http: ShAbstractHttpService,

        private search: SearchService,
        private readonly paginationService: ShPaginationService,
        private zoneRestrictions: ZoneRestrictions,
        private ZoneService: ZonesService
    ) {}

    public getRelationTypes(): Observable<RelationType[]> {
        if (!this.relationTypes$) {
            this.relationTypes$ = this.http.get<RelationType[]>(`${this.relationTypesEndpoint}`).pipe(shareReplay(1));
        }

        return this.relationTypes$;
    }

    public getRecordRelations(fragment: ApiFragment, relationTypes: RelationType[]): Observable<ApiFragment[]> {
        const relationOptions: QueryPart[] = relationTypes.map(
            relationType =>
                new QueryPart({
                    field: `Relations${relationType.RelationType}`,
                    values: [fragment.getFragmentId()]
                })
        );
        const relationsRestriction: Restriction = Combine.Or(...relationOptions);

        const restriction$: Observable<Restriction> = this.ZoneService.zones$.pipe(
            take(1),
            map(zones => {
                const statusRestrictions: Restriction[] = [
                    RecordRestrictions.phaseRestriction([RecordPhase.Published])
                ];

                // Only add ConceptRestriction if user has zones
                if (zones && zones.length > 0) {
                    statusRestrictions.push(RecordRestrictions.conceptZoneRestriction(zones));
                }
                return Combine.And(
                    relationsRestriction,
                    this.zoneRestrictions.regularRecordTypesZone(),
                    Combine.Or(...statusRestrictions)
                );
            })
        );

        const query$: Observable<ApiQuery> = restriction$.pipe(map(restriction => new ApiQuery([], restriction)));

        const sortInfo: SortInfo = {
            field: 'Title',
            direction: SortDirection.ASC
        };

        return query$.pipe(
            take(1),
            switchMap(query =>
                this.search.getAllRecords(
                    query,
                    {
                        annotate: false
                    },
                    sortInfo
                )
            )
        );
    }
}
